import Cookies from "js-cookie"
import type { PostHog } from "posthog-js"
import type { CookieConsent } from "./consentTypes"
import { scrubMagicLinkSecret } from "src/utils/magicLink"

export interface AnalyticsConfiguration {
    posthogToken: string
    posthogHost: string
    googleAnalyticsId: string
    googleAdsId: string
}

type PendingCall =
    | { kind: "capture"; eventName: string; properties?: Record<string, any> }
    | { kind: "identify"; distinctId: string; properties?: Record<string, any> }
    | { kind: "reset" }

let analyticsConfiguration: AnalyticsConfiguration | null = null
let currentConsent: CookieConsent | null = null
let posthogInstance: PostHog | null = null
let posthogLoading: Promise<PostHog | null> | null = null
let posthogInitScheduled = false
const pendingPosthogCalls: PendingCall[] = []
let googleTagScriptLoaded = false
let googleTagScriptLoading: Promise<void> | null = null

function isBrowser(): boolean {
    return typeof window !== "undefined" && typeof document !== "undefined"
}

function initializeGoogleTagRuntime() {
    if (!isBrowser()) {
        return
    }

    const win = window as Window & { dataLayer?: any[]; gtag?: (...args: any[]) => void }
    win.dataLayer = win.dataLayer || []
    if (!win.gtag) {
        win.gtag = function gtag(...args: any[]) {
            win.dataLayer?.push(args)
        }
    }
    win.gtag("js", new Date())
}

async function loadGoogleTagScript(primaryId: string): Promise<void> {
    if (!isBrowser()) {
        return
    }

    if (googleTagScriptLoaded) {
        return
    }

    if (!googleTagScriptLoading) {
        googleTagScriptLoading = new Promise<void>((resolve) => {
            const script = document.createElement("script")
            script.async = true
            script.src = primaryId
                ? `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`
                : "https://www.googletagmanager.com/gtag/js"
            script.onload = () => {
                googleTagScriptLoaded = true
                resolve()
            }
            script.onerror = () => resolve()
            document.head.appendChild(script)
        }).finally(() => {
            googleTagScriptLoading = null
        })
    }

    await googleTagScriptLoading
}

function configGoogleTag(id: string) {
    if (!isBrowser()) {
        return
    }

    const win = window as Window & { gtag?: (...args: any[]) => void }
    win.gtag?.("config", id)
}

function clearStorageKeys(keysToRemove: string[]) {
    if (!isBrowser()) {
        return
    }

    for (const storage of [window.localStorage, window.sessionStorage]) {
        const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter((key): key is string => !!key)
        keys.forEach((key) => {
            if (keysToRemove.includes(key)) {
                storage.removeItem(key)
            }
        })
    }
}

function clearCookiesByPredicate(predicate: (name: string) => boolean) {
    if (!isBrowser()) {
        return
    }

    Object.keys(Cookies.get())
        .filter(predicate)
        .forEach((name) => {
            Cookies.remove(name, { path: "/" })
        })
}

function clearAnalyticsStorage() {
    const posthogToken = analyticsConfiguration?.posthogToken
    const keysToRemove = [
        "persist:posthog",
        "persist:posthog-js",
    ]

    if (posthogToken) {
        keysToRemove.push(`ph_${posthogToken}_posthog`)
        keysToRemove.push(`ph_${posthogToken}`)
        keysToRemove.push(`__ph_opt_in_out_${posthogToken}`)
    }

    clearStorageKeys(keysToRemove)
    clearCookiesByPredicate((name) =>
        name.startsWith("ph_")
        || name.startsWith("__ph_")
        || name.startsWith("posthog")
        || name.includes("posthog")
        || name === "_ga"
        || name === "_gid"
        || name === "_gat"
    )
}

function clearMarketingStorage() {
    clearCookiesByPredicate((name) => name.startsWith("_gcl_"))
}

function scheduleIdle(callback: () => void) {
    if (!isBrowser()) {
        return
    }

    const win = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }
    if (typeof win.requestIdleCallback === "function") {
        win.requestIdleCallback(callback, { timeout: 4000 })
    } else {
        setTimeout(callback, 500)
    }
}

function flushPendingPosthogCalls(instance: PostHog) {
    while (pendingPosthogCalls.length > 0) {
        const call = pendingPosthogCalls.shift()!
        try {
            if (call.kind === "capture") {
                instance.capture(call.eventName, call.properties)
            } else if (call.kind === "identify") {
                instance.identify(call.distinctId, call.properties)
            } else if (call.kind === "reset") {
                instance.reset(true)
            }
        } catch {
            // Best-effort — swallow errors from the analytics runtime.
        }
    }
}

async function loadAndInitPostHog(): Promise<PostHog | null> {
    if (!analyticsConfiguration || !isBrowser()) {
        return null
    }

    try {
        const module = await import(/* webpackChunkName: "posthog" */ "posthog-js")
        const instance = (module as unknown as { default: PostHog }).default
        instance.init(analyticsConfiguration.posthogToken, {
            api_host: analyticsConfiguration.posthogHost,
            defaults: "2026-01-30",
            // The secret in a report email's link is a credential. It is in the address while the
            // page reads it, so every event is cleaned before it leaves the browser.
            before_send: (event) => (event ? scrubMagicLinkSecret(event) : event),
        })
        posthogInstance = instance
        flushPendingPosthogCalls(instance)
        return instance
    } catch {
        return null
    }
}

function ensurePostHogLoading(): Promise<PostHog | null> {
    if (posthogInstance) {
        return Promise.resolve(posthogInstance)
    }
    if (!posthogLoading) {
        posthogLoading = loadAndInitPostHog()
    }
    return posthogLoading
}

function schedulePostHogInit() {
    if (posthogInitScheduled || !analyticsConfiguration || !isBrowser()) {
        return
    }
    posthogInitScheduled = true
    scheduleIdle(() => {
        void ensurePostHogLoading()
    })
}

export function configureAnalytics(configuration: AnalyticsConfiguration) {
    analyticsConfiguration = configuration
    if (currentConsent) {
        syncAnalyticsWithConsent(currentConsent)
    }
}

export function syncAnalyticsWithConsent(consent: CookieConsent | null) {
    currentConsent = consent

    // TODO(beta): once consent enforcement goes live, use the banner selection
    // to gate loading/capture and retire the always-on tracking path below.
    schedulePostHogInit()

    void (async () => {
        try {
            const primaryId = analyticsConfiguration?.googleAnalyticsId ?? analyticsConfiguration?.googleAdsId
            if (!primaryId) {
                return
            }

            await loadGoogleTagScript(primaryId)
            initializeGoogleTagRuntime()
            if (analyticsConfiguration?.googleAnalyticsId) {
                configGoogleTag(analyticsConfiguration.googleAnalyticsId)
            }
            if (analyticsConfiguration?.googleAdsId) {
                configGoogleTag(analyticsConfiguration.googleAdsId)
            }
        } catch {
            // Google tags are best effort in beta.
        }
    })()
}

export function hasAnalyticsConsent(): boolean {
    return !!currentConsent?.analytics
}

export function hasMarketingConsent(): boolean {
    return !!currentConsent?.marketing
}

export function trackEvent(eventName: string, properties?: Record<string, any>) {
    if (posthogInstance) {
        posthogInstance.capture(eventName, properties)
        return
    }
    pendingPosthogCalls.push({ kind: "capture", eventName, properties })
    schedulePostHogInit()
}

export function identifyUser(distinctId: string, properties?: Record<string, any>) {
    if (posthogInstance) {
        posthogInstance.identify(distinctId, properties)
        return
    }
    pendingPosthogCalls.push({ kind: "identify", distinctId, properties })
    schedulePostHogInit()
}

export function clearAnalyticsIdentity() {
    if (posthogInstance) {
        try {
            posthogInstance.reset(true)
        } catch {
            // Ignore cleanup failures.
        }
        return
    }
    // If posthog is still loading, drop any queued identity so we don't
    // re-identify a signed-out user once it finishes.
    for (let i = pendingPosthogCalls.length - 1; i >= 0; i--) {
        if (pendingPosthogCalls[i].kind === "identify") {
            pendingPosthogCalls.splice(i, 1)
        }
    }
}

export function __resetAnalyticsForTests() {
    analyticsConfiguration = null
    currentConsent = null
    posthogInstance = null
    posthogLoading = null
    posthogInitScheduled = false
    pendingPosthogCalls.length = 0
    googleTagScriptLoaded = false
    googleTagScriptLoading = null

    if (isBrowser()) {
        document.querySelectorAll('script[src*="googletagmanager"]').forEach((node) => node.remove())
    }
}
